import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Card } from "../register/ui/card-animated";
import { Select } from "@/shared/components/organisms/Select";
import { TextArea } from "@/shared/components/atoms/TextArea";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLoader } from "@fortawesome/pro-regular-svg-icons";
import {
  useCreateLeadCampaignRegisterMutation,
  useGetCampaignRegisterFieldsQuery,
} from "./api/campaignRegister.api";
import { toasts } from "@/shared/utils/toasts";

type ApiField = {
  name: string;
  type: "text" | "email" | "number" | "date" | "select" | "textarea";
  label: string;
  required?: boolean;
  options?: { id: string; name: string }[]; // solo si es select
};

export function CampaignRegister() {
  const [searchParams] = useSearchParams();
  const [dynamicFields, setDynamicFields] = useState<ApiField[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [thanksForm, setThanksForm] = useState<boolean>(false);
  const code = searchParams.get("code");
  const media = searchParams.get("media");

  const getCampaignField = useGetCampaignRegisterFieldsQuery({
    campaignCode: code,
  });
  const [createLeadCampaign, { isLoading }] =
    useCreateLeadCampaignRegisterMutation();

  const fields = useMemo(() => {
    if (!getCampaignField.data) return [];

    const { sex, phone, comment, birthdate } = getCampaignField.data;

    const allOptions = [
      {
        name: "sex",
        type: "select",
        label: "Sexo",
        required: true,
        options: [
          { id: "1", name: "Hombre" },
          { id: "2", name: "Mujer" },
        ],
      },
      {
        name: "birthday",
        type: "date",
        label: "Fecha de Nacimiento",
        required: true,
      },
      { name: "phone", type: "tel", label: "Teléfono", required: true },
      {
        name: "comments",
        type: "textarea",
        label: "Comentarios",
        required: false,
      },
    ];

    const selected: string[] = [];
    if (sex === 1) selected.push("sex");
    if (birthdate === 1) selected.push("birthday");
    if (phone === 1) selected.push("phone");
    if (comment === 1) selected.push("comments");

    return allOptions.filter((field) => selected.includes(field.name));
  }, [getCampaignField.data]);

  useEffect(() => {
    if (fields.length > 0) {
      setDynamicFields(fields as any);
      setLoading(false);
    }
  }, [fields]);

  // Construir initialValues y validationSchema
  const initialValues: Record<string, any> = {
    fullName: "",
    email: "",
  };
  const validation: Record<string, any> = {
    fullName: Yup.string().required("El nombre es obligatorio"),
    email: Yup.string()
      .email("Correo inválido")
      .required("El correo es obligatorio"),
  };

  dynamicFields.forEach((field) => {
    initialValues[field.name] = field.type === "select" ? null : "";
    if (field.required) {
      switch (field.type) {
        case "text":
          validation[field.name] = Yup.number()
            .typeError("Debe ser un número")
            .required(`${field.label} es obligatorio`);
          break;
        case "date":
          validation[field.name] = Yup.date().required(
            `${field.label} es obligatorio`
          );
          break;
        case "select":
          validation[field.name] = Yup.object({
            id: Yup.string().required(),
            name: Yup.string().required(),
          }).required(`${field.label} es obligatorio`);
          break;
        default:
          validation[field.name] = Yup.string().required(
            `${field.label} es obligatorio`
          );
      }
    }
  });

  const handleRegisterUserCampaign = async function (values) {
    try {
      await createLeadCampaign({
        ...values,
        name: values.fullName,
        campaign_id: getCampaignField.data.id,
        sex: values.sex?.id === "1" ? "H" : "M",
        comment: values.comments,
        media,
      }).unwrap();
      toasts.success("Lead credo", "Se registró exitosamente");
      setThanksForm(true);
    } catch (error) {
      console.error(error);
      toasts.success("Error", "Ocurrió un error al registrar lead");
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object(validation),
    enableReinitialize: true, // 🔑 muy importante cuando los campos se cargan async
    onSubmit: async (values, formikHelpers) => {
      try {
        await handleRegisterUserCampaign(values);
        formikHelpers.resetForm();
      } catch (error) {
        console.error(error);
      }
    },
  });

  if (loading)
    return (
      <div className="flex flex-1 justify-center items-center">
        <FontAwesomeIcon icon={faLoader} spin className="text-4xl" />
      </div>
    );

  if (thanksForm) {
    return (
      <div className="flex flex-col items-center justify-center p-6 text-center">
        <h2 className="text-xl font-semibold text-gray-800">🎉 ¡Gracias!</h2>
        <p className="mt-2 text-gray-600">
          Tu información se ha enviado correctamente.
        </p>
      </div>
    );
  }

  if (!code) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <h2 className="text-center text-lg font-medium text-gray-700">
          Lo sentimos, este enlace no es válido o ha expirado.
          <br />
          Por favor, contacta a tu centro para confirmar el enlace e inténtalo
          nuevamente.
        </h2>
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-start justify-center mt-5">
      <form
        onSubmit={formik.handleSubmit}
        className="w-full mx-6 sm:w-[50%] flex flex-col"
      >
        <Card title="Información Personal" delay={2}>
          <div className="space-y-10">
            {/* Campos fijos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputWithCornerHint
                label="Nombre completo"
                name="fullName"
                value={formik.values.fullName}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.fullName)}
                textError={formik.errors.fullName}
                disabled={isLoading}
              />
              <InputWithCornerHint
                label="Correo Electrónico"
                name="email"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.email)}
                textError={formik.errors.email}
                disabled={isLoading}
              />
            </div>

            {/* Campos dinámicos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dynamicFields.map((field) => {
                if (field.type === "select") {
                  return (
                    <Select
                      addDefault={false}
                      key={field.name}
                      name={field.name}
                      tittle={field.label}
                      options={field.options || []}
                      selected={formik.values[field.name]}
                      setSelected={
                        (option) => formik.setFieldValue(field.name, option) // ✅ guardar objeto completo
                      }
                      error={Boolean(formik.errors[field.name])}
                      textError={formik.errors[field.name] as string}
                      disabled={isLoading}
                    />
                  );
                }

                if (field.type === "textarea") {
                  return (
                    <TextArea
                      key={field.name}
                      label={field.label}
                      name={field.name}
                      placeholder={field.label}
                      value={formik.values[field.name]}
                      onChange={formik.handleChange}
                      className="w-full border rounded p-2"
                      disabled={isLoading}
                    />
                  );
                }

                return (
                  <InputWithCornerHint
                    key={field.name}
                    label={field.label}
                    name={field.name}
                    type={field.type}
                    value={formik.values[field.name]}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors[field.name])}
                    textError={formik.errors[field.name]}
                    required={field.required}
                    disabled={isLoading}
                  />
                );
              })}
            </div>
          </div>
        </Card>
        <button
          type="submit"
          className="mt-6 px-8 py-2 bg-primary self-end text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading && (
            <span className="mr-2">
              <FontAwesomeIcon icon={faLoader} spin className="text-lg" />
            </span>
          )}
          Enviar
        </button>
      </form>
    </div>
  );
}
